/**
 * PopupVesselTimelinePage - Stateless vessel timeline popup
 *
 * Displays vessel timeline (port calls, inspections, bans, sanctions),
 * integrity status cards, and AIS track visualization.
 * Receives all data from the main window via BroadcastChannel.
 * Does NOT create its own WebSocket/API connections.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { Button, LoadingSpinner, ScrollArea, useToast } from '@ais-ui/ui-core';
import { X } from 'lucide-react';
import { useLocalization } from '@ais-ui/locale-management';
import { formatVesselName } from '@ais-ui/utils';
import {
  PopupType,
  getPopupIdFromUrl,
  getPopupParams,
  closePopup,
} from '@ais-ui/popups';
import {
  PopupActions,
  MessageType,
  useMainWindowMessages,
  PopupMessage,
  DataType,
} from './PopupEvents';
import { usePopupLifecycle } from './usePopupLifecycle';
import { PopupConnectionStatus } from './shared/PopupConnectionStatus';
import { usePopupPerformanceTracking } from './usePopupPerformanceTracking';
import { usePopupDataState } from './usePopupDataState';
import { PopupRefreshIndicator } from './PopupRefreshIndicator';
import { TimelineContainer } from '../../components/TimelineContainer';
import { DateRange } from 'react-day-picker';
import { PopupBidirectionalMapChart } from './PopupBidirectionalMapChart';
import { TargetTab } from '../Targets/targetTypes';
import { PopupIntegrityStatusCards } from './VesselIntegrityCards';
import { VesselDetailsSidebar } from './VesselDetailsSidebar';
import { ShipInfo } from '@ais-ui/websockets';
import { useVesselTimelineEvents } from './useVesselTimelineEvents';

// ============================================================================
// Types
// ============================================================================
import {
  PopupVesselTimelineData,
  deserializeShipInfo,
} from './popupVesselTypes';

// ============================================================================
// Main Component
// ============================================================================

export const PopupVesselTimelinePage: React.FC = () => {
  const popupId = getPopupIdFromUrl();
  const params = getPopupParams();
  const { messages } = useLocalization();
  const { toast } = useToast();

  // Use the popup data state hook for "stale while revalidate" pattern
  const {
    data,
    isInitialLoading,
    isRefreshing,
    error,
    setData,
    setRefreshing,
    setError,
  } = usePopupDataState<PopupVesselTimelineData>();

  const dataReceivedRef = useRef(false);
  const dataKeyRef = useRef<string | undefined>(undefined);
  const pendingShipTrackRequestRef = useRef<string | null>(null);
  const [sidebarTab, setSidebarTab] = useState<TargetTab>('base');
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [chartShipTrack, setChartShipTrack] = useState<{
    coordinates: (string | number)[][];
  } | null>(null);
  const [chartDataKey, setChartDataKey] = useState<string | undefined>(
    undefined,
  );

  // Memoize metadata to prevent performance tracking from re-creating callbacks
  const trackingMetadata = useMemo(
    () => ({
      imo: params.imo || 'unknown',
      mmsi: params.mmsi || 'unknown',
    }),
    [params.imo, params.mmsi],
  );

  // Performance tracking
  const { trackDataRequest, trackDataReceived, trackError, trackTimeout } =
    usePopupPerformanceTracking({
      popupId,
      popupType: PopupType.VesselTimeline,
      metadata: trackingMetadata,
    });

  // Local date range state for filtering timeline events (7 days)
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);
    today.setHours(23, 59, 59, 999);
    return { from: sevenDaysAgo, to: today };
  });

  // Separate date range state for the AIS chart (defaults to last 7 days)
  const [chartDateRange, setChartDateRange] = useState<DateRange | undefined>(
    () => {
      const now = new Date();
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return { from: sevenDaysAgo, to: now };
    },
  );

  // Request initial data from main window
  useEffect(() => {
    if (!popupId) {
      setError('No popup ID found');
      trackError('No popup ID found');
      return;
    }

    dataReceivedRef.current = false;
    trackDataRequest();

    // Use default chart date range for initial request
    PopupActions.requestInit({
      popupId,
      popupType: PopupType.VesselTimeline,
      params: {
        ...params,
        dateFrom: dateRange?.from?.toISOString(),
        dateTo: dateRange?.to?.toISOString(),
        chartDateFrom: chartDateRange?.from?.toISOString(),
        chartDateTo: chartDateRange?.to?.toISOString(),
      },
    });

    const timeout = setTimeout(() => {
      if (!dataReceivedRef.current) {
        setError('Timeout waiting for data from main window.');
        trackTimeout();
      }
    }, 15000);

    return () => clearTimeout(timeout);
  }, [popupId, trackDataRequest, trackError, trackTimeout]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle chart date range change - request new ship track data from backend
  const handleChartDateRangeChange = useCallback(
    (newDateRange: DateRange | undefined) => {
      if (!popupId || !newDateRange?.from || !newDateRange?.to) {
        setChartDateRange(newDateRange);
        return;
      }

      // Use exact times from the datetime picker (user picks both date and time)
      setChartDateRange(newDateRange);

      // Show chart-specific loading indicator
      setIsChartLoading(true);

      // Request new ship track data using requestData (not requestInit)
      const requestId = PopupActions.requestData(popupId, DataType.SHIP_TRACK, {
        imo: params.imo,
        mmsi: params.mmsi,
        chartDateFrom: newDateRange.from.toISOString(),
        chartDateTo: newDateRange.to.toISOString(),
      });

      // Track the pending request
      pendingShipTrackRequestRef.current = requestId;
    },
    [popupId, params.imo, params.mmsi],
  );

  // Handle date range change - request new data from backend
  const handleDateRangeChange = useCallback(
    (newDateRange: DateRange | undefined) => {
      if (!popupId || !newDateRange?.from || !newDateRange?.to) {
        setDateRange(newDateRange);
        return;
      }

      // Update local state immediately for UI feedback
      setDateRange(newDateRange);

      // Show refreshing indicator (only if we have data already)
      setRefreshing(true);
      dataReceivedRef.current = false;

      // Keep current chart date range when timeline changes
      PopupActions.requestInit({
        popupId,
        popupType: PopupType.VesselTimeline,
        params: {
          ...params,
          dateFrom: newDateRange.from.toISOString(),
          dateTo: newDateRange.to.toISOString(),
          chartDateFrom: chartDateRange?.from?.toISOString(),
          chartDateTo: chartDateRange?.to?.toISOString(),
        },
      });
    },
    [popupId, params, chartDateRange, setRefreshing],
  );

  // Listen for messages from main window
  useMainWindowMessages(
    (msg: PopupMessage) => {
      // Handle initial data response
      if (
        msg.type === MessageType.POPUP_INIT_RESPONSE &&
        msg.popupId === popupId
      ) {
        dataReceivedRef.current = true;
        setIsChartLoading(false);
        const payload = msg.payload as PopupVesselTimelineData;
        if (payload.error) {
          setError(payload.error);
          trackError(payload.error);
        } else {
          // Only update if dataKey is different to avoid unnecessary re-renders (flickering)
          if (payload.dataKey !== dataKeyRef.current) {
            dataKeyRef.current = payload.dataKey;
            setData(payload);
            // Initialize chart data from initial response
            setChartShipTrack(payload.shipTrack || null);
            setChartDataKey(payload.chartDataKey);
            trackDataReceived(payload.portCalls?.length);
            // Update document title
            document.title = payload.vessel.name
              ? `${formatVesselName(payload.vessel.name)} - Timeline`
              : `Vessel ${payload.vessel.imo} - Timeline`;
          } else {
            // Same data, just clear the refreshing state
            setRefreshing(false);
          }
        }
        return;
      }

      // Handle ship track data response (for chart date range changes)
      if (msg.type === MessageType.DATA_RESPONSE && msg.popupId === popupId) {
        const payload = msg.payload as {
          requestId: string;
          data: {
            shipTrack: { coordinates: (string | number)[][] } | null;
            chartDataKey: string;
            error?: string;
          };
        };

        // Check if this is the response we're waiting for
        if (payload.requestId !== pendingShipTrackRequestRef.current) return;

        pendingShipTrackRequestRef.current = null;
        setIsChartLoading(false);

        if (payload.data.error) {
          console.error(
            '[PopupVesselTimelinePage] Ship track error:',
            payload.data.error,
          );
          toast({
            title: 'Failed to load track data',
            description:
              'Unable to fetch AIS track data for the selected date range.',
            variant: 'destructive',
          });
        } else {
          setChartShipTrack(payload.data.shipTrack);
          setChartDataKey(payload.data.chartDataKey);
        }
      }
    },
    popupId ?? undefined,
    [
      popupId,
      setData,
      setError,
      setRefreshing,
      trackDataReceived,
      trackError,
      toast,
    ],
  );

  // Handle popup lifecycle (close notification, heartbeat)
  const connectionState = usePopupLifecycle(popupId, PopupType.VesselTimeline);

  // Timeline event conversion
  const {
    eventTypes: EVENT_TYPES,
    allEvents,
    filteredEvents,
  } = useVesselTimelineEvents({ data, dateRange });

  // Convert serialized shipInfo back to ShipInfo object for BaseInformation
  const shipInfoForPanel: ShipInfo | null = useMemo(() => {
    return deserializeShipInfo(data?.shipInfo);
  }, [data?.shipInfo]);

  // Use local chartShipTrack state which is updated independently for date range changes
  const memoizedShipTrack = useMemo(() => chartShipTrack, [chartShipTrack]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <div className="text-destructive text-lg font-medium mb-4">
          {messages.popupGenericError}
        </div>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={closePopup}>{messages.popupGenericCloseWindow}</Button>
      </div>
    );
  }

  if (isInitialLoading || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <LoadingSpinner className="h-8 w-8" />
        <p className="mt-4 text-muted-foreground">
          {messages.popupVesselTimelineLoading}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-primary/5 via-background to-background relative">
      {/* Connection status overlay */}
      <PopupConnectionStatus connectionState={connectionState} />

      {/* Refresh indicator overlay */}
      <PopupRefreshIndicator
        isRefreshing={isRefreshing}
        message={messages.popupVesselTimelineRefreshing}
      />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {formatVesselName(data.vessel.name) || `Vessel ${data.vessel.imo}`}
          </h1>
          <p className="text-sm text-muted-foreground">
            {messages.popupVesselTimelineImo?.replace(
              '{imo}',
              String(data.vessel.imo),
            )}
            {data.vessel.mmsi && (
              <>
                {' '}
                •{' '}
                {messages.popupVesselTimelineMmsi?.replace(
                  '{mmsi}',
                  String(data.vessel.mmsi),
                )}
              </>
            )}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={closePopup}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Content - Two column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main content area */}
        <ScrollArea className="flex-1">
          <div className="p-6 space-y-6">
            {/* Timeline - first component */}
            <TimelineContainer
              key={`timeline-${filteredEvents.length}`}
              isLoading={false}
              events={filteredEvents}
              eventTypes={EVENT_TYPES}
              className="mb-4"
              title={messages.popupVesselTimelineEventTimeline}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              listMode="vessel"
              viewModeStorageKey="popup-vessel-timeline-view-mode"
              emptyMessage={
                allEvents.length === 0
                  ? messages.popupVesselTimelineNoEvents
                  : messages.popupVesselTimelineNoEventsInRange
              }
            />

            {/* Integrity Status Cards (ban, sanctions, etc.) */}
            <PopupIntegrityStatusCards
              advancedIntegrity={data.advancedIntegrity}
              hasPermission={data.permissions?.mdmIntegrity || false}
            />

            {/* AIS Track Chart */}
            <PopupBidirectionalMapChart
              shipTrack={memoizedShipTrack}
              dateRange={chartDateRange}
              onDateRangeChange={handleChartDateRangeChange}
              isLoading={isChartLoading}
              chartKey={chartDataKey}
            />
          </div>
        </ScrollArea>

        {/* Vessel Details Sidebar */}
        {shipInfoForPanel && (
          <VesselDetailsSidebar
            shipInfo={shipInfoForPanel}
            tab={sidebarTab}
            onTabChange={setSidebarTab}
            permissions={data.permissions}
            advancedVessel={data.advancedVessel}
            advancedIntegrity={data.advancedIntegrity}
            advancedRegister={data.advancedRegister}
          />
        )}
      </div>
    </div>
  );
};

export default PopupVesselTimelinePage;
